import { Check, ChevronDown, Plus } from "lucide-react";
import { useRef, useState } from "react";
import type { SenderNameMode, SlackIdentity } from "../../../shared/types";
import { api } from "../../api";
import { Avatar, LoadingRaccoon } from "../../components/primitives";

const choices: Array<{ mode: SenderNameMode; label: string; value: (sender: SlackIdentity) => string }> = [
  { mode: "display", label: "Display name", value: (sender) => sender.displayName },
  { mode: "real", label: "Full name", value: (sender) => sender.realName },
  { mode: "username", label: "Username", value: (sender) => sender.username },
];

function squareCrop(file: File): Promise<{ blob: Blob; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const size = Math.min(image.naturalWidth, image.naturalHeight);
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Could not prepare the image."));
        return;
      }
      const sourceX = (image.naturalWidth - size) / 2;
      const sourceY = (image.naturalHeight - size) / 2;
      ctx.drawImage(image, sourceX, sourceY, size, size, 0, 0, 512, 512);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) resolve({ blob, mimeType: "image/png" });
        else reject(new Error("Could not crop the image."));
      }, "image/png");
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    image.src = url;
  });
}

export function SenderNamePicker({
  sender,
  identities,
  canManageIdentities = false,
  disabled = false,
  onChange,
  onSelectIdentity,
  onClearIdentity,
  onChanged,
}: {
  sender: SlackIdentity;
  identities?: Array<{ id: string; name: string; avatarUrl: string }>;
  canManageIdentities?: boolean;
  disabled?: boolean;
  onChange: (sender: SlackIdentity) => void;
  onSelectIdentity?: (identity: { id: string; name: string; avatarUrl: string }) => void;
  onClearIdentity?: () => void;
  onChanged?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState<SenderNameMode | "identity">();
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const choose = async (mode: SenderNameMode) => {
    if (saving) return;
    if (mode === sender.nameMode) {
      setOpen(false);
      return;
    }
    setSaving(mode);
    setError("");
    try {
      const result = await api.senderName(mode);
      onClearIdentity?.();
      onChange({
        ...sender,
        name: result.selectedName,
        nameMode: result.mode,
        selectedName: result.selectedName,
      });
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save this name.");
    } finally {
      setSaving(undefined);
    }
  };

  const chooseIdentity = async (identity: { id: string; name: string; avatarUrl: string }) => {
    if (saving) return;
    if (sender.id === identity.id && sender.nameMode === "identity") {
      setOpen(false);
      return;
    }
    setSaving("identity");
    setError("");
    try {
      onSelectIdentity?.(identity);
      onChange({
        ...sender,
        id: identity.id,
        name: identity.name,
        username: identity.name,
        displayName: identity.name,
        realName: identity.name,
        selectedName: identity.name,
        nameMode: "identity",
        avatarUrl: identity.avatarUrl,
      });
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not select this identity.");
    } finally {
      setSaving(undefined);
    }
  };

  const createIdentity = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving("identity");
    setError("");
    try {
      const created = await api.createIdentity(name);
      if (avatarFile) {
        const { blob, mimeType } = await squareCrop(avatarFile);
        await api.uploadIdentityAvatar(created.id, blob, mimeType);
      }
      const fresh = (await api.identities()).find((item) => item.id === created.id);
      const identity = fresh ?? { ...created, avatarUrl: "" };
      onChanged?.();
      await chooseIdentity(identity);
      setCreating(false);
      setNewName("");
      setAvatarFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create this identity.");
    } finally {
      setSaving(undefined);
    }
  };

  const isIdentity = sender.nameMode === "identity";

  return (
    <div className="sender-name-picker">
      <button
        type="button"
        className="posting-as"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? `Posted as ${sender.selectedName}` : `Posting as ${sender.selectedName} — change identity`}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{sender.selectedName}</span>
        <Avatar name={sender.selectedName} src={sender.avatarUrl} size={30} />
        {!disabled && <ChevronDown size={12} />}
      </button>
      {open && !disabled && (
        <>
          <button
            type="button"
            className="sender-name-backdrop"
            aria-label="Close sender name menu"
            onClick={() => setOpen(false)}
          />
          <div className="sender-name-menu" role="menu" aria-label="Send as">
            <header>Send as</header>
            {creating ? (
              <section className="sender-name-create">
                <input
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void createIdentity();
                    if (event.key === "Escape") setCreating(false);
                  }}
                  placeholder="Identity name"
                  maxLength={120}
                />
                <div className="sender-name-create-actions">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) => setAvatarFile(event.target.files?.[0] ?? null)}
                  />
                  <button type="button" onClick={() => fileRef.current?.click()}>
                    {avatarFile ? "Change photo" : "Add photo"}
                  </button>
                  <button type="button" className="sender-name-create-submit" onClick={() => void createIdentity()}>
                    {saving === "identity" ? <LoadingRaccoon size={15} /> : "Create"}
                  </button>
                </div>
              </section>
            ) : (
              canManageIdentities && (
                <>
                  {identities && identities.length > 0 && (
                    <section className="sender-name-identities">
                      {identities.map((identity) => {
                        const active = isIdentity && sender.id === identity.id;
                        return (
                          <button
                            type="button"
                            role="menuitemradio"
                            aria-checked={active}
                            key={identity.id}
                            onClick={() => void chooseIdentity(identity)}
                          >
                            <Avatar name={identity.name} src={identity.avatarUrl} size={24} />
                            <span>
                              <strong>{identity.name}</strong>
                              <small>Custom identity</small>
                            </span>
                            {saving === "identity" ? <LoadingRaccoon size={16} /> : active && <Check size={14} />}
                          </button>
                        );
                      })}
                      <div className="sender-name-identity-sep" aria-hidden="true" />
                    </section>
                  )}
                  <button type="button" role="menuitem" onClick={() => setCreating(true)}>
                    <Plus size={14} />
                    <span>
                      <strong>New identity</strong>
                      <small>Create a custom name + photo</small>
                    </span>
                  </button>
                </>
              )
            )}
            {!creating && (
              <>
                <div className="sender-name-identity-sep" aria-hidden="true" />
                {choices.map((choice) => {
                  const value = choice.value(sender) || sender.selectedName;
                  const active = !isIdentity && sender.nameMode === choice.mode;
                  return (
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      key={choice.mode}
                      onClick={() => void choose(choice.mode)}
                    >
                      <span>
                        <strong>{value}</strong>
                        <small>{choice.label}</small>
                      </span>
                      {saving === choice.mode ? <LoadingRaccoon size={16} /> : active && <Check size={14} />}
                    </button>
                  );
                })}
              </>
            )}
            {error && <p>{error}</p>}
          </div>
        </>
      )}
    </div>
  );
}
